import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import { 
  DialogActions, DialogContent, DialogTitle, Select, Option,
  Grid, Chip, ToggleButtonGroup,
} from '@mui/joy';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Sheet from '@mui/joy/Sheet';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TuneIcon from '@mui/icons-material/Tune';
import { useSelector, useDispatch } from 'react-redux';
import { getPolicies } from '../redux/policiesReducer';
import { Stack, CircularProgress } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import request from '../helpers/request';
import Autocomplete from '@mui/joy/Autocomplete';

import PolicyCard from './PolicyCard';
import { useDialogKeyboard } from '../hooks/useDialogKeyboard';
import PoliciesEmptyState from './PoliciesEmptyState';
import CreatePolicyDrawer from './CreatePolicyDrawer';

const OrderTable = () => {
  const [search, setSearch] = useState('');
  const [openCreatePolicy, setOpenCreatePolicy] = useState(false);
  const [duplicatePolicy, setDuplicatePolicy] = useState(null);
  const [deletePolicy, setDeletePolicy] = useState(null);
  const { policies, isPoliciesLoading } = useSelector(state => state.policies);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openBulkRerun, setOpenBulkRerun] = useState(false);
  const [bulkRerunPolicy] = useState(null);
  const [bulkRerunForm, setBulkRerunForm] = useState({ start_id: '', end_id: '', fields: [] });
  const [bulkRerunLoading, setBulkRerunLoading] = useState(false);
  const [bulkRerunError, setBulkRerunError] = useState('');
  const [bulkRerunSuccess, setBulkRerunSuccess] = useState('');
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskTypesLoading, setTaskTypesLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(getPolicies());
  }, [dispatch]);

  useEffect(() => {
    const fetchTaskTypes = async () => {
      setTaskTypesLoading(true);
      try {
        const response = await request.get('/api/v1/tools/tasktype/');
        setTaskTypes(response.data);
      } catch (e) {
        console.error('Error fetching task types:', e);
      }
      setTaskTypesLoading(false);
    };
    fetchTaskTypes();
  }, []);

  const handleCloseCreatePolicy = () => {
    setOpenCreatePolicy(false);
  };
  
  const moveToWizard = (policy, metadata = {}) => {
    const isTemplatePrefill = metadata?.isTemplatePrefill === true;
    const path = policy?.id
      ? `/app/policies/wizard/${policy.id}`
      : '/app/policies/wizard';
    navigate(path, { state: { policy, isTemplatePrefill } });
  };

  const handleDelete = async () => {
    try {
      await request.delete(`/api/v1/compliance/policy/${deletePolicy.id}/`);
      dispatch(getPolicies());
    } catch (e) {
      console.error("Error in deleting a policy: ", e);
      alert("Something went wrong");
    }
    onCancelDelete();
  };

  const handleDuplicate = async () => {
    if (!duplicatePolicy) return;
    try {
      const newPolicy = {
        ...duplicatePolicy,
        id: undefined,
        name: `${duplicatePolicy.name} copy`,
      };

      await request.post('/api/v1/compliance/policy/', newPolicy);
      dispatch(getPolicies());
      setDuplicatePolicy(null);
    } catch (e) {
      console.error('Error duplicating policy:', e);
      alert('Failed to duplicate policy');
    }
    onCancelDuplicate();
  };

  const onCancelDuplicate = () => {
    setDuplicatePolicy(null);
  };

  const onCancelDelete = () => {
    setDeletePolicy(null);
  };

  useDialogKeyboard(!!deletePolicy, { onEnter: handleDelete, onEscape: onCancelDelete });

  const handleFilter = (policy) => {
    const searchLower = search.toLowerCase();
    if (!search) return true;
    if (`${policy.id}`.includes(searchLower)) return true;
    if (policy.name?.toLowerCase().includes(searchLower)) return true;
    if (policy.settings?.type?.toLowerCase().includes(searchLower)) return true;
    if (policy.policy?.description?.toLowerCase().includes(searchLower)) return true;
    return false;
  };

  const handleBulkRerunFieldChange = (field, value) => {
    setBulkRerunForm(prev => ({ ...prev, [field]: value }));
  };

  const handleBulkRerunSubmit = async () => {
    setBulkRerunLoading(true);
    setBulkRerunError('');
    setBulkRerunSuccess('');
    try {
      const { start_id, end_id, fields } = bulkRerunForm;
      if (!start_id || !end_id || !fields.length) {
        setBulkRerunError('All fields are required.');
        setBulkRerunLoading(false);
        return;
      }
      const res = await request.post(
        '/api/v1/tools/task/bulk-reprocess-fields/',
        { start_id: Number(start_id), end_id: Number(end_id), fields }
      );
      setBulkRerunSuccess(res.message || 'Bulk rerun started.');
      setTimeout(() => {
        navigate('/app/tasks');
      }, 500);
    } catch (e) {
      setBulkRerunError(e?.response?.data?.error || 'Bulk rerun failed.');
    }
    setBulkRerunLoading(false);
  };

  const isStartIdInvalid = Number(bulkRerunForm.start_id) < 1;
  const isEndIdInvalid = Number(bulkRerunForm.end_id) < 1;
  const isEndBeforeStart =
    bulkRerunForm.start_id &&
    bulkRerunForm.end_id &&
    Number(bulkRerunForm.end_id) < Number(bulkRerunForm.start_id);

  const handleChangeRowsPerPage = (newValue) => {
    const updatedValue = Number(newValue);
    if (updatedValue > 0) {
      setRowsPerPage(updatedValue);
      setPage(0);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const labelDisplayedRows = ({ from, to, count }) => `${from}-${to} of ${count}`;

  const handleAddPolicy = () => {
    setOpenCreatePolicy(true);
  };

  const filteredPolicies = policies.filter(handleFilter);
  const paginatedPolicies = filteredPolicies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Show empty state when no policies
  if (!isPoliciesLoading && policies.length === 0) {
    return (
      <>
        <PoliciesEmptyState onAddNew={handleAddPolicy} />
        <CreatePolicyDrawer
          open={openCreatePolicy}
          onClose={handleCloseCreatePolicy}
          taskTypes={taskTypes}
          taskTypesLoading={taskTypesLoading}
          policies={policies}
          onSubmitPolicy={moveToWizard}
        />
      </>
    );
  }

  return (
    <Box>
      {/* Modals */}
      <Modal open={!!deletePolicy} onClose={onCancelDelete}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle sx={{ marginInlineEnd: 4 }}>You are about to delete a policy</DialogTitle>
          <DialogContent>
            You are about to delete policy {deletePolicy?.name}.
            <br />
            Are you sure you want to delete it?
          </DialogContent>
          <DialogActions>
            <Button color='danger' onClick={handleDelete}>Delete</Button>
            <Button variant='plain' onClick={onCancelDelete}>Cancel</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!duplicatePolicy} onClose={onCancelDuplicate}>
        <ModalDialog>
          <ModalClose onClick={onCancelDuplicate} />
          <DialogTitle>Duplicate Policy</DialogTitle>
          <DialogContent>
            Are you sure you want to duplicate the policy{' '}
            <strong>{duplicatePolicy?.name}?</strong>
          </DialogContent>
          <DialogActions>
            <Button color="success" onClick={handleDuplicate}>
              Confirm
            </Button>
            <Button variant="plain" onClick={onCancelDuplicate}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <CreatePolicyDrawer
        open={openCreatePolicy}
        onClose={handleCloseCreatePolicy}
        taskTypes={taskTypes}
        taskTypesLoading={taskTypesLoading}
        policies={policies}
        onSubmitPolicy={moveToWizard}
      />

      <Modal open={openBulkRerun} onClose={() => setOpenBulkRerun(false)}>
        <ModalDialog>
          <DialogTitle>Bulk Rerun for Policy: {bulkRerunPolicy?.name}</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Input
                type="number"
                placeholder="Start Task ID"
                value={bulkRerunForm.start_id}
                min={1}
                onChange={e => {
                  const val = e.target.value.replace(/^(-)/, '');
                  handleBulkRerunFieldChange('start_id', val);
                }}
                error={isStartIdInvalid}
              />
              <Input
                type="number"
                placeholder="End Task ID"
                value={bulkRerunForm.end_id}
                min={1}
                onChange={e => {
                  const val = e.target.value.replace(/^(-)/, '');
                  handleBulkRerunFieldChange('end_id', val);
                }}
                error={isEndIdInvalid || isEndBeforeStart}
              />
              {isEndBeforeStart && (
                <Typography color="danger" level="body3" sx={{ mt: 1 }}>
                  End Task ID cannot be less than Start Task ID.
                </Typography>
              )}
              <Autocomplete
                multiple
                options={
                  Array.isArray(bulkRerunPolicy?.policy?.documentFieldsSchema)
                    ? bulkRerunPolicy.policy.documentFieldsSchema
                        .filter(f => f && f.key && f.type)
                        .map(f => f.key)
                    : []
                }
                value={bulkRerunForm.fields}
                onChange={(_, value) => handleBulkRerunFieldChange('fields', value)}
                placeholder="Fields to re-process"
                freeSolo
              />
              {bulkRerunError && (
                <Typography color="danger">{bulkRerunError}</Typography>
              )}
              {bulkRerunSuccess && (
                <Typography color="success">{bulkRerunSuccess}</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setOpenBulkRerun(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              loading={bulkRerunLoading}
              onClick={handleBulkRerunSubmit}
              disabled={
                bulkRerunLoading ||
                isStartIdInvalid ||
                isEndIdInvalid ||
                isEndBeforeStart ||
                !bulkRerunForm.start_id ||
                !bulkRerunForm.end_id ||
                !bulkRerunForm.fields.length
              }
            >
              Rerun
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 3 }}
      >
        <Input
          placeholder="Search policies..."
          value={search}
          startDecorator={<SearchIcon sx={{ color: 'neutral.500' }} />}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />

        <ToggleButtonGroup
          value={viewMode}
          onChange={(_, val) => val && setViewMode(val)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          <IconButton value="grid" aria-label="Grid view">
            <GridViewRoundedIcon />
          </IconButton>
          <IconButton value="list" aria-label="List view">
            <ViewListRoundedIcon />
          </IconButton>
        </ToggleButtonGroup>

        <IconButton
          variant="outlined"
          color="neutral"
          onClick={() => dispatch(getPolicies())}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          <RefreshIcon />
        </IconButton>

        <Button
          startDecorator={<AddRoundedIcon />}
          onClick={handleAddPolicy}
          color="primary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create
        </Button>
      </Stack>

      {/* Content */}
      <Box>
        {isPoliciesLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size="lg" />
            <Typography level="body-md" sx={{ mt: 2 }} color="neutral">
              Loading policies...
            </Typography>
          </Stack>
        ) : filteredPolicies.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <Typography level="body-lg" color="neutral">
              No policies match your search
            </Typography>
            <Button
              variant="plain"
              onClick={() => setSearch('')}
              sx={{ mt: 1 }}
            >
              Clear search
            </Button>
          </Stack>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <Grid container spacing={2}>
                {paginatedPolicies.map((policy, index) => (
                  <Grid key={policy.id} xs={12} sm={6} md={4} lg={3}>
                    <PolicyCard
                      policy={policy}
                      onEdit={moveToWizard}
                      onDuplicate={setDuplicatePolicy}
                      onDelete={setDeletePolicy}
                      index={index}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* List View - Compact cards */}
            {viewMode === 'list' && (
              <Stack spacing={1}>
                {paginatedPolicies.map((policy) => (
                  <Sheet
                    key={policy.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 'md',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: 'background.level1',
                        borderColor: 'primary.300',
                      },
                    }}
                    onClick={() => moveToWizard(policy)}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 'sm',
                        bgcolor: 'primary.softBg',
                        color: 'primary.600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <TuneIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                        {policy.name}
                      </Typography>
                      <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {policy.policy?.description || 'No description'}
                      </Typography>
                    </Box>
                    <Chip size="sm" variant="soft" color="neutral">
                      {policy.settings?.type || 'Unknown'}
                    </Chip>
                    <Typography level="body-xs" color="neutral" sx={{ minWidth: 50 }}>
                      ID: {policy.id}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDuplicatePolicy(policy);
                        }}
                      >
                        ⧉
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletePolicy(policy);
                        }}
                      >
                        ×
                      </IconButton>
                    </Stack>
                  </Sheet>
                ))}
              </Stack>
            )}

            {/* Pagination */}
            {filteredPolicies.length > rowsPerPage && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'flex-end',
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <FormControl orientation="horizontal" size="sm">
                  <FormLabel>Per page:</FormLabel>
                  <Select
                    value={rowsPerPage}
                    onChange={(_, newValue) => handleChangeRowsPerPage(newValue)}
                    sx={{ minWidth: 70 }}
                  >
                    <Option value={6}>6</Option>
                    <Option value={12}>12</Option>
                    <Option value={24}>24</Option>
                    <Option value={48}>48</Option>
                  </Select>
                </FormControl>
                <Typography level="body-sm" sx={{ textAlign: 'center', minWidth: 80 }}>
                  {labelDisplayedRows({
                    from: filteredPolicies.length === 0 ? 0 : page * rowsPerPage + 1,
                    to: Math.min(filteredPolicies.length, (page + 1) * rowsPerPage),
                    count: filteredPolicies.length,
                  })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => handleChangePage(page - 1)}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    disabled={page >= Math.ceil(filteredPolicies.length / rowsPerPage) - 1}
                    onClick={() => handleChangePage(page + 1)}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default OrderTable;
